const isSlideBreakBlock = (block) =>
  Boolean(block && block.type === 'divider' && block.content?.isSlideBreak === true);

/**
 * Has a section's first slide been emptied out of existence?
 *
 * Every slide but the first is recorded by the slide-break divider in front of
 * it: delete every block on slide 4 and the divider still says slide 4 is
 * there, so it stays as a blank slide. The **first** slide of a section has no
 * divider — `flattenSlidesToBlocks` writes one only for `index > 0` — so its
 * existence is implied purely by its blocks. Delete the last one and the slide
 * is not emptied, it is erased, taking its name, background and duration with
 * it and sliding every following slide up a place.
 *
 * (That is also why creating a slide used to seed a text box: a slide with
 * nothing on it was not a slide the deck could hold on to.)
 *
 * The signature: a healthy section never *begins* with a slide-break divider,
 * because the first slide's own blocks come first. One at the front means the
 * blocks that used to precede it are gone.
 *
 * Two things it must not mistake for that.
 *
 * **A slide the author deliberately deleted.** `handleRemoveSlide` drops the
 * removed slide's blocks and the divider in front of them — and for the first
 * slide there is no divider in front, so it would leave exactly this shape.
 * That is why it now also drops the divider that would be left leading: a
 * deliberate deletion leaves no leading divider at all, and cannot be confused
 * with an emptied slide.
 *
 * **The repaired state.** Restoring the slide puts a divider at the front, so
 * a rule of "starts with a slide break" would fire again on its own output and
 * prepend for ever. A restored first slide is blank, so the divider at the
 * front introduces a slide with no blocks; the lost one is followed by the
 * blocks of the slide that took its place. That is the discriminator, and it
 * terminates.
 *
 * `knownFirstSlideId` closes the one case the shape alone cannot: a first
 * slide emptied while the second is *also* blank leaves a leading divider
 * introducing an empty slide. If the id the first slide had is no longer
 * anywhere in the section, it is gone whatever the shape says.
 *
 * @param {Array<object>} blocks - a section's block list
 * @param {string|null} [knownFirstSlideId] - the id its first slide last had
 * @returns {boolean}
 */
export const hasLostFirstSlide = (blocks = [], knownFirstSlideId = null) => {
  const list = Array.isArray(blocks) ? blocks : [];
  if (!isSlideBreakBlock(list[0])) return false;

  // The slide the leading divider introduces. Blocks after it mean it is the
  // slide that moved up into first place, not a blank one left behind.
  if (!isSlideBreakBlock(list[1]) && list.length > 1) return true;

  if (!knownFirstSlideId) return false;
  return !list.some((block) => (
    block?.slideId === knownFirstSlideId || block?.content?.slideId === knownFirstSlideId
  ));
};

export const segmentBlocksIntoSlides = (blocks = [], { includeEmpty = true } = {}) => {
  const slides = [];
  let current = [];
  let nextSlideId = null;

  const pushCurrent = (forceEmpty = false) => {
    if (!current.length && !forceEmpty) return;
    slides.push({ blocks: current, slideId: nextSlideId });
    current = [];
    nextSlideId = null;
  };

  blocks.forEach((block) => {
    const isSlideBreak = block?.type === 'divider' && block?.content?.isSlideBreak === true;
    if (isSlideBreak) {
      if (current.length > 0) {
        pushCurrent();
      } else if (includeEmpty && nextSlideId !== null) {
        pushCurrent(true);
      }
      nextSlideId = block.slideId || block.content?.slideId || null;
      return;
    }

    if (!current.length && nextSlideId === null && (block?.slideId || block?.content?.slideId)) {
      nextSlideId = block.slideId || block.content?.slideId;
    }

    current.push(block);
  });

  if (current.length > 0) {
    pushCurrent();
  } else if (includeEmpty && (slides.length === 0 || nextSlideId !== null)) {
    pushCurrent(true);
  }

  return slides;
};

/**
 * The slide id a section's first slide is currently using, or null.
 *
 * Remembered before a change so that, if the first slide is emptied out of
 * existence, the divider that restores it can carry the id it had — which is
 * what keeps that slide's name, background and duration attached to it.
 */
export const firstSlideIdOf = (blocks = []) => {
  const [first] = segmentBlocksIntoSlides(blocks, { includeEmpty: true });
  return first?.slideId || null;
};
